// FaceLock Scan resume: the frames captured so far, kept in this browser's
// IndexedDB so leaving mid-scan loses nothing. It never leaves the device.
// Everything is wrapped: a private window, a full disk or an old browser
// simply means no resume, never a broken scan. Pictures are stored as bytes
// (older Safari could not keep Blobs in IndexedDB). A saved scan belongs to
// the account that made it, and expires after 7 days.
//
// One record per picture (database "facelock-scan-v2"): `meta` holds the
// scan's fields and every frame's fields but its picture, `frames` holds each
// picture's bytes under the frame's id. A save writes only the pictures that
// are new or replaced since the last one, and drops the ones the scan let go,
// in one transaction with the meta. The old database ("facelock-scan", one
// record holding everything) is read once, moved here, and never upgraded: a
// bundle still cached on a phone opens it at its version 1 and keeps its own
// resume.

import { bucketById, cleanPlan, type ScanBucketId, type ScanPlan } from "@/shared/face-qc/index.mjs";
import { emptySet, type ScanFrame, type ScanSet } from "./session";

const DB = "facelock-scan";
const STORE = "scan";
const KEY = "current";
const VERSION = 1;
export const SAVED_SCAN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const DB_V2 = "facelock-scan-v2";
const META = "meta";
const FRAMES = "frames";
const META_VERSION = 2;
/** Saving stops for the rest of the page's life after this many failed saves in a row (savingStopped). */
const FAILS_TO_STOP = 2;

type StoredFrame = Omit<ScanFrame, "blob"> & { bytes: ArrayBuffer; type: string };
export interface StoredScan {
  v: number;
  savedAt: number;
  owner: string | null;
  mode: ScanSet["mode"];
  baselinePitch: number | null;
  baselineDescriptor: number[] | null;
  frames: StoredFrame[];
}

/** The `meta` record of the v2 database: the scan without its pictures (frames in the set's order). */
export interface StoredMetaV2 {
  v: 2;
  savedAt: number;
  owner: string | null;
  mode: ScanSet["mode"];
  baselinePitch: number | null;
  baselineDescriptor: number[] | null;
  plan: ScanPlan | null;
  frames: Array<Omit<ScanFrame, "blob">>;
}

/** One picture of the v2 `frames` store, under its frame's id. */
export interface StoredPicture { bytes: ArrayBuffer; type: string }

/** Who is signed in, from the session token's payload (no verification: it only keys a local copy). */
export function currentOwner(): string | null {
  let token = "";
  try { token = localStorage.getItem("auth_token") || ""; } catch { return null; }
  const part = token.split(".")[1];
  if (!part) return token ? `t:${token.length}` : null;
  try {
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    const id = json.id ?? json.userId ?? json.user_id ?? json.sub ?? json.uid;
    return id !== undefined && id !== null ? String(id) : null;
  } catch {
    return null;
  }
}

async function blobBytes(b: Blob): Promise<ArrayBuffer> {
  if (typeof b.arrayBuffer === "function") return b.arrayBuffer();
  // Older Safari (and jsdom): no Blob.arrayBuffer.
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.onerror = () => reject(r.error);
    r.readAsArrayBuffer(b);
  });
}

export async function toStored(set: ScanSet, now = Date.now(), owner = currentOwner()): Promise<StoredScan> {
  const frames: StoredFrame[] = [];
  for (const f of set.frames) {
    const { blob, ...meta } = f;
    frames.push({ ...meta, bytes: await blobBytes(blob), type: blob.type || "image/jpeg" });
  }
  return { v: VERSION, savedAt: now, owner, mode: set.mode, baselinePitch: set.baselinePitch, baselineDescriptor: set.baselineDescriptor, frames };
}

/** A stored scan back as a set; null when it is someone else's, too old, or unreadable. */
export function fromStored(rec: unknown, now = Date.now(), owner = currentOwner()): ScanSet | null {
  const r = rec as StoredScan | null;
  if (!r || r.v !== VERSION || !Array.isArray(r.frames) || typeof r.savedAt !== "number") return null;
  if (now - r.savedAt > SAVED_SCAN_TTL_MS) return null;
  if ((r.owner ?? null) !== (owner ?? null)) return null;
  const set = emptySet(r.mode === "upload" ? "upload" : "scan");
  const frames: ScanFrame[] = r.frames
    .filter((f) => f && bucketById(f.bucket) && f.bytes)
    .map(({ bytes, type, ...meta }) => ({ ...meta, bucket: meta.bucket as ScanBucketId, blob: new Blob([bytes], { type: type || "image/jpeg" }) }));
  return { ...set, frames, baselinePitch: r.baselinePitch ?? null, baselineDescriptor: r.baselineDescriptor ?? null };
}

/** The v2 `meta` record of a set: every frame's fields but its picture, the plan cleaned (none = null). */
export function toMeta(set: ScanSet, now = Date.now(), owner = currentOwner()): StoredMetaV2 {
  return {
    v: META_VERSION, savedAt: now, owner, mode: set.mode, baselinePitch: set.baselinePitch, baselineDescriptor: set.baselineDescriptor,
    plan: set.plan ? cleanPlan(set.plan) : null,
    frames: set.frames.map(({ blob, ...meta }) => meta),
  };
}

/**
 * The v2 record back as a set, with each picture from `bytesById`; null when it is someone else's, too old, or
 * not a v2 record. A frame whose picture is missing, or whose bucket is unknown, is left out (its view reopens).
 */
export function fromMeta(rec: unknown, bytesById: Map<string, StoredPicture>, now = Date.now(), owner = currentOwner()): ScanSet | null {
  const r = rec as StoredMetaV2 | null;
  if (!r || r.v !== META_VERSION || !Array.isArray(r.frames) || typeof r.savedAt !== "number") return null;
  if (now - r.savedAt > SAVED_SCAN_TTL_MS) return null;
  if ((r.owner ?? null) !== (owner ?? null)) return null;
  const frames: ScanFrame[] = [];
  for (const f of r.frames) {
    const pic = f && typeof f.id === "string" ? bytesById.get(f.id) : undefined;
    if (!pic || !pic.bytes || !bucketById(f.bucket)) continue;
    frames.push({ ...f, bucket: f.bucket as ScanBucketId, blob: new Blob([pic.bytes], { type: pic.type || "image/jpeg" }) });
  }
  const set: ScanSet = { ...emptySet(r.mode === "upload" ? "upload" : "scan"), frames, baselinePitch: r.baselinePitch ?? null, baselineDescriptor: r.baselineDescriptor ?? null };
  if (r.plan) set.plan = cleanPlan(r.plan);
  return set;
}

/**
 * What a save writes: `put` the frames whose picture is not on disk, or is not the very Blob last written for
 * that id (a picture replaced under the same id is written again); `remove` the ids on disk the set no longer
 * holds. A frame whose other fields changed (a server reason, say) is not put: those travel in the meta.
 */
export function planSave(set: ScanSet, onDisk: string[], written: Map<string, Blob>): { put: ScanFrame[]; remove: string[] } {
  const disk = new Set(onDisk);
  const held = new Set(set.frames.map((f) => f.id));
  return {
    put: set.frames.filter((f) => !disk.has(f.id) || written.get(f.id) !== f.blob),
    remove: onDisk.filter((id) => !held.has(id)),
  };
}

function openDb(name: string, create: (db: IDBDatabase) => void, existingOnly = false): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined" || !indexedDB) return resolve(null);
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = (ev) => {
        // Only reading what an older bundle left: a database that is not there is not made.
        if (existingOnly && ev.oldVersion === 0) { try { req.transaction?.abort(); } catch { /* resolved below */ } return; }
        try { create(req.result); } catch { /* resolved below */ }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

const openV2 = () => openDb(DB_V2, (db) => {
  if (!db.objectStoreNames.contains(META)) db.createObjectStore(META);
  if (!db.objectStoreNames.contains(FRAMES)) db.createObjectStore(FRAMES);
});
const openV1 = (existingOnly = true) => openDb(DB, (db) => {
  if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
}, existingOnly);

const close = (db: IDBDatabase) => { try { db.close(); } catch { /* closed */ } };

/** One transaction on `db` over `stores`: `fn` issues the requests, its answer comes back when the transaction completes (null when it fails). */
function txn<T>(db: IDBDatabase | null, stores: string[], mode: IDBTransactionMode, fn: (tx: IDBTransaction, done: (v: T) => void) => void): Promise<T | null> {
  return new Promise<T | null>((resolve) => {
    if (!db) return resolve(null);
    let out: T | null = null;
    try {
      const tx = db.transaction(stores, mode);
      tx.oncomplete = () => { close(db); resolve(out); };
      tx.onabort = () => { close(db); resolve(null); };
      tx.onerror = () => { /* the abort that follows resolves */ };
      fn(tx, (v) => { out = v; });
    } catch {
      close(db);
      resolve(null);
    }
  });
}

/** The old database's record, or null (none, or no IndexedDB). */
async function readV1(): Promise<unknown> {
  return txn<unknown>(await openV1(), [STORE], "readonly", (tx, done) => {
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => done(req.result ?? null);
  });
}

async function clearV1(): Promise<void> {
  await txn<boolean>(await openV1(), [STORE], "readwrite", (tx, done) => { tx.objectStore(STORE).delete(KEY); done(true); });
}

async function clearV2(): Promise<boolean> {
  const ok = await txn<boolean>(await openV2(), [META, FRAMES], "readwrite", (tx, done) => {
    tx.objectStore(META).clear();
    tx.objectStore(FRAMES).clear();
    done(true);
  });
  if (ok) written.clear();
  return ok === true;
}

// The picture last written for each id by this page (or read back by it): what planSave compares with.
const written = new Map<string, Blob>();
let failsInARow = 0;
let stopped = false;

/** True once two saves in a row have failed: nothing more is saved while this page lives (nothing is said to the person). */
export const savingStopped = (): boolean => stopped;

let stopTold = false;
/** True exactly once after saving stopped, for this page's whole life like the stop itself (the telemetry's `store_fail`). */
export function takeSavingStoppedNotice(): boolean {
  if (!stopped || stopTold) return false;
  stopTold = true;
  return true;
}

/** Write `set` as the diff against what is on disk. False when it could not be written. */
async function writeScan(set: ScanSet): Promise<boolean> {
  // 1. What is on disk.
  const onDisk = await txn<string[]>(await openV2(), [FRAMES], "readonly", (tx, done) => {
    const req = tx.objectStore(FRAMES).getAllKeys();
    req.onsuccess = () => done((req.result as IDBValidKey[]).map(String));
  });
  if (!onDisk) return false;
  // 2. What to write, and 3. its bytes, read before the transaction (a transaction ends while the code waits on anything else).
  const { put, remove } = planSave(set, onDisk, written);
  const pictures: Array<[ScanFrame, StoredPicture]> = [];
  for (const f of put) pictures.push([f, { bytes: await blobBytes(f.blob), type: f.blob.type || "image/jpeg" }]);
  const meta = toMeta(set);
  // 4. One transaction: the deletes, the puts, the meta.
  const ok = await txn<boolean>(await openV2(), [META, FRAMES], "readwrite", (tx, done) => {
    const frames = tx.objectStore(FRAMES);
    for (const id of remove) frames.delete(id);
    for (const [f, pic] of pictures) frames.put(pic, f.id);
    tx.objectStore(META).put(meta, KEY);
    done(true);
  });
  if (!ok) return false;
  for (const id of remove) written.delete(id);
  for (const [f] of pictures) written.set(f.id, f.blob);
  return true;
}

// Saves (and clears) run one after another. A save that a later save or clear was asked for before it started is
// skipped: the later one writes against what is on disk, so nothing is lost.
let tail: Promise<unknown> = Promise.resolve();
let asked = 0;
function queued<T>(job: (latest: boolean) => Promise<T>): Promise<T> {
  const mine = ++asked;
  const p = tail.then(() => job(mine === asked));
  tail = p.catch(() => undefined);
  return p;
}

/** Keep the scan so far. An empty set clears it. */
export function saveScan(set: ScanSet): Promise<boolean> {
  if (!set.frames.length) return clearSavedScan().then(() => true);
  return queued(async (latest) => {
    if (stopped) return false;
    if (!latest) return true;
    let ok = false;
    try { ok = await writeScan(set); } catch { ok = false; }
    failsInARow = ok ? 0 : failsInARow + 1;
    if (failsInARow >= FAILS_TO_STOP) stopped = true;
    return ok;
  });
}

/** The saved v2 scan: the set (null when there is none or it is not usable) and whether a record was there at all. */
async function readV2(): Promise<{ set: ScanSet | null; found: boolean }> {
  const got = await txn<{ meta: unknown; pictures: Map<string, StoredPicture> }>(await openV2(), [META, FRAMES], "readonly", (tx, done) => {
    const pictures = new Map<string, StoredPicture>();
    const req = tx.objectStore(META).get(KEY);
    req.onsuccess = () => {
      const meta = req.result ?? null;
      done({ meta, pictures });
      const frames = (meta as StoredMetaV2 | null)?.frames;
      if (!Array.isArray(frames)) return;
      const store = tx.objectStore(FRAMES);
      for (const f of frames) {
        if (!f || typeof f.id !== "string") continue;
        const r = store.get(f.id);
        r.onsuccess = () => { if (r.result) pictures.set(f.id, r.result as StoredPicture); };
      }
    };
  });
  if (!got || got.meta === null || got.meta === undefined) return { set: null, found: false };
  return { set: fromMeta(got.meta, got.pictures), found: true };
}

export async function loadScan(): Promise<ScanSet | null> {
  try {
    // A save still on its way lands first.
    await tail;
    const v2 = await readV2();
    if (v2.found) {
      if (!v2.set) await clearSavedScan();
      const set = v2.set && v2.set.frames.length ? v2.set : null;
      // What was read is what is on disk: the next save does not write it again.
      if (set) for (const f of set.frames) if (!written.has(f.id)) written.set(f.id, f.blob);
      return set;
    }
    // Nothing here yet: a scan an older version of the page saved is moved over, once.
    const rec = await readV1();
    if (rec === null || rec === undefined) return null;
    const set = fromStored(rec);
    if (!set || !set.frames.length) {
      await clearV1();
      return null;
    }
    if (await saveScan(set)) await clearV1();
    return set;
  } catch {
    return null;
  }
}

/** Forget the saved scan (after the avatar is created, or Start over): the pictures, the meta and the old record. */
export function clearSavedScan(): Promise<void> {
  return queued(async () => {
    try {
      await clearV2();
      await clearV1();
    } catch { /* nothing to clear */ }
  });
}
